import React, { useContext, useEffect, useMemo, useState } from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import { StylesDocuments } from "../StylesDocumentsPdf";
import { DateToString } from "../../functions/FonctionsDateHeure";
import DocumentPdf from "../DocumentPdf";
import LoaderGlobal from "../../Design/Progression/LoaderGlobal";
import PopupMessageErreur from "../../Design/Messages/PopupMessageErreur";
import { CabinetsContext } from "../../Data/CabinetContext";
import { formatterTelephone, pluriel } from "../../functions/Formatter";

export default function DocumentJournalDesReglements({
  listeReglements,
  filtreModeReglement,
  dateDebut,
  dateFin,
  open,
  onClose,
  autoPrint,
}) {
  // Styles "communs" de l’app (héritage)
  const baseStyles = useMemo(() => StyleSheet.create(StylesDocuments), []);
  // Styles spécifiques à ce doc (react-pdf safe)
  const styles = useMemo(() => StyleSheet.create(localStyles), []);

  const { getObjetCabinet } = useContext(CabinetsContext);

  const [messageErreur, setMessageErreur] = useState();
  const [cabinet, setCabinet] = useState();

  // Chargement cabinet
  useEffect(() => {
    if (!open) return;
    try {
      setCabinet(getObjetCabinet());
    } catch (e) {
      setMessageErreur(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Données + totaux (sans state inutile)
  const data = useMemo(() => (Array.isArray(listeReglements) ? listeReglements : []), [listeReglements]);

  const { totalDebit, totalCredit, total } = useMemo(() => {
    let debit = 0;
    let credit = 0;
    for (const r of data) {
      const m = Number(r?.montant ?? 0);
      if (m >= 0) credit += m;
      else debit += m;
    }
    return { totalDebit: debit, totalCredit: credit, total: credit + debit };
  }, [data]);

  const handleClose = () => {
    // évite de garder des infos en mémoire entre ouvertures
    setCabinet(undefined);
    if (onClose) onClose();
  };

  const handlePopupMessageErreurClose = () => {
    setMessageErreur(undefined);
    handleClose();
  };

  if (messageErreur && open) {
    return (
      <PopupMessageErreur
        messageErreur={messageErreur}
        onClose={handlePopupMessageErreurClose}
      />
    );
  }

  if (open && (!cabinet || !listeReglements)) {
    return <LoaderGlobal isLoading={true} />;
  }

  return (
    <DocumentPdf openDocumentPdf={open} onClose={handleClose} autoPrint={autoPrint}>
      <Document>
        <Page size="A4" style={baseStyles.page}>
          {/* HEADER */}
          <View style={styles.headerRow}>
            {/* Bloc cabinet */}
            <View style={styles.cabinetBlock}>
              <Text style={baseStyles.titreBloc}>{cabinet?.nom}</Text>

              <Text>{cabinet?.adresse?.rue}</Text>
              <Text>
                {(cabinet?.adresse?.code_postal ?? "") + " " + (cabinet?.adresse?.ville ?? "")}
              </Text>

              {!!cabinet?.telephone?.numero && (
                <Text>Tél : {formatterTelephone(cabinet?.telephone?.numero)}</Text>
              )}
              {!!cabinet?.fax?.numero && (
                <Text>Fax : {formatterTelephone(cabinet?.fax?.numero)}</Text>
              )}

              <Text>{cabinet?.email}</Text>
              <Text>{cabinet?.texte_horaire}</Text>
              <Text>{cabinet?.texte_rdv}</Text>

              {!!cabinet?.texte_libre && (
                <Text
                  style={[
                    baseStyles.informationPrincipale,
                    styles.cabinetFreeText,
                  ]}
                >
                  {cabinet?.texte_libre}
                </Text>
              )}
            </View>

            {/* Bloc titre */}
            <View style={styles.titleBlock}>
              <View style={styles.titleInner}>
                <Text style={baseStyles.titre}>JOURNAL</Text>
                <Text style={baseStyles.titre}>DES REGLEMENTS</Text>

                {!!filtreModeReglement && (
                  <Text style={baseStyles.titreBloc}>{filtreModeReglement}</Text>
                )}

                <View style={styles.dateRow}>
                  <Text>
                    {DateToString(dateDebut, {
                      year: "numeric",
                      month: "numeric",
                      day: "numeric",
                    })}{" "}
                  </Text>

                  {dateFin !== dateDebut && (
                    <Text>
                      {" au " +
                        DateToString(dateFin, {
                          year: "numeric",
                          month: "numeric",
                          day: "numeric",
                        })}
                    </Text>
                  )}
                </View>
              </View>
            </View>
          </View>

          {/* ENTETES COLONNES */}
          <View style={styles.columnsHeader}>
            <Text style={[baseStyles.titreColonneLeft, { width: 60 }]}>Date</Text>
            <Text style={[baseStyles.titreColonneLeft, { width: 330 }]}>Règlements</Text>
            <Text style={[baseStyles.titreColonneRight, { width: 60 }]}>Débit</Text>
            <Text style={[baseStyles.titreColonneRight, { width: 60 }]}>Crédit</Text>
          </View>

          {/* LIGNES */}
          {data.map((ligne) => (
            <View
              key={ligne?._id ?? `${ligne?.date}-${ligne?.montant}-${Math.random()}`}
              wrap={false}
              style={styles.row}
            >
              <Text style={[baseStyles.informationSecondaire, { width: 60 }]}>
                {DateToString(ligne?.date, {
                  day: "numeric",
                  month: "numeric",
                  year: "numeric",
                })}
              </Text>

              <View style={styles.reglementDetails}>
                <Text style={[baseStyles.normal, { width: 330 }]}>
                  {(ligne?.libelle_type_reglement ?? "") +
                    " " +
                    (ligne?.libelle_mode_reglement ?? "") +
                    " du " +
                    DateToString(ligne?.date, {
                      year: "numeric",
                      month: "numeric",
                      day: "numeric",
                    })}
                </Text>

                {Array.isArray(ligne?.cotations) &&
                  ligne.cotations.map((cotation) => (
                    <Text
                      key={cotation?._id ?? `${cotation?.cotation_date}-${cotation?.cotation_montant}`}
                      style={[baseStyles.informationSecondaire, { width: 330 }]}
                    >
                      Consultation du {DateToString(cotation?.cotation_date)} de{" "}
                      {(cotation?.cotation_patient?.nom ?? "") +
                        " " +
                        (cotation?.cotation_patient?.prenom ?? "")}{" "}
                      ({cotation?.cotation_montant} €)
                    </Text>
                  ))}
              </View>

              <Text style={[baseStyles.normalRight, { width: 60 }]}>
                {Number(ligne?.montant ?? 0) < 0 ? `${ligne.montant} €` : "-"}
              </Text>

              <Text style={[baseStyles.normalRight, { width: 60 }]}>
                {Number(ligne?.montant ?? 0) > 0 ? `${ligne.montant} €` : "-"}
              </Text>
            </View>
          ))}

          {/* TOTAUX */}
          <View style={styles.totalsRow} wrap={false}>
            <Text style={[baseStyles.informationPrincipale, { width: 400 }]}>
              {pluriel(data?.length, "règlement", "règlements") +
                " pour un total de : " +
                total +
                " €"}
            </Text>
            <Text style={[baseStyles.informationPrincipale, styles.right60]}>
              {totalDebit} €
            </Text>
            <Text style={[baseStyles.informationPrincipale, styles.right60]}>
              {totalCredit} €
            </Text>
          </View>

          {/* FOOTER */}
          <View style={styles.footer} fixed>
            <Text>
              {(cabinet?.informations_juridiques?.nom_entite_juridique ?? "") +
                ", " +
                (cabinet?.informations_juridiques?.adresse_siege ?? "") +
                ". Capital : " +
                (cabinet?.informations_juridiques?.capital_social ?? "") +
                " - N° SIRET : " +
                (cabinet?.informations_juridiques?.numero_siret ?? "")}
            </Text>

            <Text
              style={styles.footerPageNumber}
              render={({ pageNumber, totalPages }) => `Page ${pageNumber} / ${totalPages}`}
            />
          </View>
        </Page>
      </Document>
    </DocumentPdf>
  );
}

const localStyles = {
  headerRow: {
    flexDirection: "row",
  },

  cabinetBlock: {
    flexDirection: "column",
    flexGrow: 1,
    marginRight: 30,
  },

  cabinetFreeText: {
    borderTopWidth: 1,
    borderTopColor: "#D8D8D8",
    marginTop: 10,
    paddingTop: 10,
    textAlign: "center",
  },

  titleBlock: {
    flexGrow: 1,
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
  },

  titleInner: {
    flexDirection: "column",
    alignItems: "center",
    minHeight: 60,
  },

  dateRow: {
    flexDirection: "row",
  },

  columnsHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    height: 20,
    backgroundColor: "#EEEEEE",
    paddingHorizontal: 10,
    marginTop: 20,
  },

  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: 30,
    borderTopWidth: 1,
    borderTopColor: "#D8D8D8",
    paddingVertical: 5,
    paddingHorizontal: 10,
  },

  reglementDetails: {
    flexDirection: "column",
    width: 330,
  },

  totalsRow: {
    flexDirection: "row",
    alignItems: "center",
    borderTopWidth: 1,
    borderTopColor: "#000000",
    borderBottomWidth: 1,
    borderBottomColor: "#000000",
    minHeight: 20,
    paddingHorizontal: 10,
  },

  right60: {
    width: 60,
    textAlign: "right",
  },

  footer: {
    width: "100%",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 8,
    position: "absolute",
    bottom: "10mm",
    borderTopWidth: 1,
    borderTopColor: "#000000",
    left: "10mm",
    paddingTop: 5,
  },

  footerPageNumber: {
    fontSize: 8,
    fontWeight: "bold",
  },
};
